import React, { createContext, useContext, useEffect, useState } from 'react';
import firebase from 'firebase/compat/app';
import { auth, getUserProfile } from '../services/firebase';
import { UserTier } from '../types';

interface AuthContextType {
  user: firebase.User | null;
  loading: boolean;
  credits: number;
  role: 'user' | 'admin';
  tier: UserTier;
  refreshCredits: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  credits: 0,
  role: 'user',
  tier: 'free',
  refreshCredits: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<firebase.User | null>(null);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(0);
  const [role, setRole] = useState<'user' | 'admin'>('user');
  const [tier, setTier] = useState<UserTier>('free');

  const refreshCredits = async () => {
    if (user) {
      const profile = await getUserProfile(user.uid);
      setCredits(profile?.credits || 0);
      setRole(profile?.role || 'user');
      setTier(profile?.tier || 'free');
    }
  };

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }

    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const profile = await getUserProfile(currentUser.uid);
        setCredits(profile?.credits || 0);
        setRole(profile?.role || 'user');
        setTier(profile?.tier || 'free');
      } else {
        setCredits(0);
        setRole('user');
        setTier('free');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, credits, role, tier, refreshCredits }}>
      {children}
    </AuthContext.Provider>
  );
};